import Activity from '../../model/Activity'
import ActivityLog from '../../model/ActivityLog'
import Announcement from '../../model/Announcement'
import AppConfig from '../../model/AppConfig'
import Badge from '../../model/Badge'
import Challenge from '../../model/Challenge'
import ChallengeBucket from '../../model/ChallengeBucket'
import CouponCode from '../../model/CouponCode'
import AppEvent from '../../model/Event'
import Checkpoint from '../../model/Checkpoint'
import Gate from '../../model/Gate'
import GateSubmission from '../../model/GateSubmission'
import Leaderboard from '../../model/Leaderboard'
import Message from '../../model/Message'
import Notification from '../../model/Notification'
import Reaction from '../../model/Reaction'
import RestDay from '../../model/RestDay'
import Team from '../../model/Team'
import TeamMembership from '../../model/TeamMembership'
import User from '../../model/User'
import UserEngagement from '../../model/UserEngagement'
import { supabase } from '../../supabase'
import DataSource from '../domain/DataSource'
import ErrorLogEntry from '../domain/ErrorLogEntry'

const TEAM_WITH_MEMBERS = '*, team_members(users(*))'

export default class SupabaseDataSource implements DataSource {
  // -- Challenge
  async getChallenge(challengeID: string): Promise<Challenge> {
    const { data, error } = await supabase
      .from('challenges')
      .select('*')
      .eq('id', challengeID)
      .single()
    if (error) throw error
    if (!data) throw new Error('Challenge not found')
    return Challenge.fromPG(data)
  }

  async getChallenges(): Promise<Challenge[]> {
    const { data, error } = await supabase
      .from('challenges')
      .select('*')
      .order('start_date', { ascending: true })
    if (error) throw error
    if (!data) return []
    return data.map((row) => Challenge.fromPG(row))
  }

  async getAppConfig(): Promise<AppConfig> {
    const { data, error } = await supabase.from('app_config').select('*').single()
    if (error) throw error
    if (!data) throw new Error('App config not found')
    return AppConfig.fromPG(data)
  }

  async getChallengeBuckets(challengeID: string): Promise<ChallengeBucket[]> {
    const { data, error } = await supabase
      .from('challenge_buckets')
      .select('*')
      .eq('challenge_id', challengeID)
      .order('sort_order', { ascending: true })
    if (error) throw error
    if (!data) return []
    return data.map((row) => ChallengeBucket.fromPG(row))
  }

  // -- User
  async getUsers() {
    const { data, error } = await supabase.from('users').select('*')
    if (error) throw error
    if (!data) return []
    return data.map((user) => User.fromPG(user))
  }

  async getUser(userID: string) {
    const { data, error } = await supabase.from('users').select('*').eq('id', userID).single()
    if (error) throw error
    if (!data) throw new Error('User not found')
    return User.fromPG(data)
  }
  async getUserByAuthID(authID: string): Promise<User> {
    const { data, error } = await supabase.from('users').select('*').eq('id', authID).single()
    if (error) {
      void this.logError({
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        context: { source: 'SupabaseDataSource', authID, hint: 'Often means auth.users has the user but public.users does not (e.g. row deleted).', caller: 'getUserByAuthID' },
        source: 'client',
      })
      throw error
    }
    if (!data) {
      const err = new Error('User not found')
      void this.logError({
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined,
        context: { source: 'SupabaseDataSource', authID, hint: 'No row in public.users for this auth id.', caller: 'getUserByAuthID' },
        source: 'client',
      })
      throw err
    }
    return User.fromPG(data)
  }

  async saveUser(user: User) {
    const { error } = await supabase.from('users').upsert(user.toPG())
    if (error) throw error
    return
  }

  async deleteUser(userID: string) {
    const { error } = await supabase.from('users').delete().eq('id', userID)
    if (error) throw error
    return
  }

  // -- Membership / entitlement
  async getTeamMemberships(userID: string): Promise<TeamMembership[]> {
    const { data, error } = await supabase
      .from('team_members')
      .select('*')
      .eq('user_id', userID)
    if (error) throw error
    if (!data) return []
    return data.map((row) => TeamMembership.fromPG(row))
  }

  async getTeamMembershipForChallenge(
    userID: string,
    challengeID: string,
  ): Promise<TeamMembership | null> {
    const { data, error } = await supabase
      .from('team_members')
      .select('*')
      .eq('user_id', userID)
      .eq('challenge_id', challengeID)
      .maybeSingle()
    if (error) throw error
    if (!data) return null
    return TeamMembership.fromPG(data)
  }

  async addTeamMember(userID: string, teamID: string, challengeID: string): Promise<void> {
    const { error } = await supabase.from('team_members').upsert(
      {
        user_id: userID,
        team_id: teamID,
        challenge_id: challengeID,
      },
      { onConflict: 'user_id,challenge_id' },
    )
    if (error) throw error
  }

  async removeTeamMember(userID: string, teamID: string): Promise<void> {
    const { error } = await supabase
      .from('team_members')
      .delete()
      .eq('user_id', userID)
      .eq('team_id', teamID)
    if (error) throw error
  }

  async isUserEntitled(userID: string, challengeID: string): Promise<boolean> {
    const { data: purchase, error: purchaseError } = await supabase
      .from('purchases')
      .select('id')
      .eq('user_id', userID)
      .eq('challenge_id', challengeID)
      .eq('status', 'fulfilled')
      .limit(1)
      .maybeSingle()
    if (purchaseError) throw purchaseError
    if (purchase) return true

    const membership = await this.getTeamMembershipForChallenge(userID, challengeID)
    if (!membership) return false

    const team = await this.getTeam(membership.teamID)
    return team?.hasPaid === true
  }

  async getFulfilledPurchaseChallengeIds(userID: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('purchases')
      .select('challenge_id')
      .eq('user_id', userID)
      .eq('status', 'fulfilled')
    if (error) throw error
    if (!data) return []
    return [...new Set(data.map((row) => row.challenge_id as string).filter(Boolean))]
  }

  // -- Activity
  async getActivities(challengeID: string) {
    const { data, error } = await supabase
      .from('activities')
      .select('*, buckets(id, name)')
      .or(`challenge_id.eq.${challengeID},challenge_id.is.null`)
      .order('order', { ascending: true, nullsFirst: false })
    if (error) throw error
    if (!data) return []
    return data.map((activity) => Activity.fromPG(activity))
  }

  async getActivityLogs(teamID: string) {
    const { data, error } = await supabase.from('activity_logs').select('*').eq('team_id', teamID)
    if (error) throw error
    if (!data) return []
    return data!.map((activityLog) => ActivityLog.fromPG(activityLog))
  }

  async saveActivityLog(activityLog: ActivityLog) {
    const { error } = await supabase.from('activity_logs').insert(activityLog.toPG())
    if (error) throw error
    return
  }

  async deleteActivityLog(activityLogID: string) {
    const { data, error } = await supabase
      .from('activity_logs')
      .delete()
      .eq('id', activityLogID)
      .select('id')
    if (error) throw error
    if (!data?.length) throw new Error(`Activity log ${activityLogID} was not deleted`)
    return
  }

  // -- Team
  async getTeams(challengeID: string) {
    const { data, error } = await supabase
      .from('teams')
      .select(TEAM_WITH_MEMBERS)
      .eq('challenge_id', challengeID)
    if (error) throw error
    if (!data) return []
    return data.map((team) => Team.fromPG(team))
  }

  async getTeam(teamID: string) {
    const { data, error } = await supabase
      .from('teams')
      .select(TEAM_WITH_MEMBERS)
      .eq('id', teamID)
      .single()
    if (error) throw error
    if (!data) return null
    return Team.fromPG(data)
  }

  async saveTeam(team: Team) {
    const { error } = await supabase.from('teams').insert(team.toPG())
    if (error) throw error
    return
  }

  async updateTeamName(teamID: string, name: string) {
    await this.updateTeam(teamID, { name })
  }

  async updateTeamLeader(teamID: string, leaderID: string) {
    await this.updateTeam(teamID, { leader_id: leaderID })
  }

  private async updateTeam(teamID: string, changes: { name?: string; leader_id?: string }) {
    const { data, error } = await supabase
      .from('teams')
      .update(changes)
      .eq('id', teamID)
      .select('id')
    if (error) throw error
    if (!data?.length) throw new Error(`Team ${teamID} was not updated`)
  }

  async getLeaderboard(challengeID: string, restDaysRequired: number): Promise<Leaderboard> {
    const { data, error } = await supabase
      .from('teams')
      .select(
        `
        id, 
        name, 
        activity_logs(amount, activity_id),
        team_members(
          users(
            id,
            users_rest_days (id, challenge_id)
          )
        )
      `,
      )
      .eq('challenge_id', challengeID)

    if (error) throw error
    if (!data) throw new Error('No data found')

    const teams = data.map((team: any) => {
      const members = (team.team_members ?? [])
        .map((row: any) => row.users)
        .filter(Boolean)
      const memberCount = members.length

      const progress = (team.activity_logs ?? []).reduce(
        (sum: number, log: any) => sum + log.amount,
        0,
      )

      const activityBreakdown: { [activityId: string]: number } = {}
      ;(team.activity_logs ?? []).forEach((log: any) => {
        if (!activityBreakdown[log.activity_id]) {
          activityBreakdown[log.activity_id] = 0
        }
        activityBreakdown[log.activity_id] += log.amount
      })

      const restDaysForChallenge = (member: any) =>
        (member.users_rest_days ?? []).filter((rd: any) => rd.challenge_id === challengeID)

      const restDayCount = members.reduce(
        (sum: number, member: any) => sum + restDaysForChallenge(member).length,
        0,
      )
      const membersCompletedRestDays = members.reduce(
        (sum: number, member: any) =>
          sum + (restDaysForChallenge(member).length >= restDaysRequired ? 1 : 0),
        0,
      )
      const restDayTarget = memberCount * restDaysRequired

      return {
        id: team.id,
        name: team.name,
        progress,
        memberCount,
        activityBreakdown,
        restDayCount,
        restDayTarget,
        membersCompletedRestDays,
      }
    })

    const leaderProgress = teams.length > 0 ? Math.max(...teams.map((team) => team.progress)) : 0
    teams.sort((a, b) => b.progress - a.progress)

    return new Leaderboard({
      leaderProgress,
      teams,
    })
  }

  // -- Messages (limit 5000 to avoid Supabase default 1000-row cap; order oldest-first so newest are in last 1000)
  async getMessages(teamID: string): Promise<Message[]> {
    const { data, error } = await supabase
      .from('messages')
      .select('*, users (*)')
      .eq('team_id', teamID)
      .order('created_at', { ascending: true })
      .range(0, 4999)
    if (error) throw error
    if (!data) return []
    return data.map((messageData) => Message.fromPG(messageData))
  }

  async saveMessage(message: Message): Promise<void> {
    const { error } = await supabase.from('messages').insert(message.toPG())
    if (error) throw error
  }

  onNewMessage(teamID: string, callback: (message: Message) => void): any {
    const subscription = supabase
      .channel(`public:messages:${teamID}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `team_id=eq.${teamID}`,
        },
        (payload) => {
          const newMessageData = payload.new

          supabase
            .from('users')
            .select('*')
            .eq('id', newMessageData.user_id)
            .single()
            .then(({ data: userData, error: userError }) => {
              if (userError) {
                void this.logError({
                  message: userError instanceof Error ? userError.message : String(userError),
                  stack: userError instanceof Error ? userError.stack : undefined,
                  context: { source: 'SupabaseDataSource', action: 'fetchUserForMessage', userId: newMessageData.user_id },
                  source: 'client',
                })
                return
              }

              if (!userData) {
                void this.logError({
                  message: 'User data not found',
                  context: { source: 'SupabaseDataSource', userId: newMessageData.user_id },
                  source: 'client',
                })
                return
              }
              const messageDataWithUser = { ...newMessageData, users: userData }
              const newMessage = Message.fromPG(messageDataWithUser)

              callback(newMessage)
            })
        },
      )
      .subscribe()

    return subscription
  }

  // -- Reactions
  async getReactions(messageId: string): Promise<Reaction[]> {
    const { data, error } = await supabase
      .from('reactions')
      .select('*')
      .eq('message_id', messageId)
      .order('created_at', { ascending: true })
    if (error) throw error
    if (!data) return []
    return data.map((reactionData) => Reaction.fromPG(reactionData))
  }

  async saveReaction(reaction: Reaction): Promise<Reaction> {
    const { data, error } = await supabase
      .from('reactions')
      .insert(reaction.toPG())
      .select()
      .single()
    if (error) throw error
    return Reaction.fromPG(data)
  }

  async updateReaction(reaction: Reaction): Promise<void> {
    const { error } = await supabase.from('reactions').update(reaction.toPG()).eq('id', reaction.id)
    if (error) throw error
  }

  async deleteReaction(reactionId: number): Promise<void> {
    const { error } = await supabase.from('reactions').delete().eq('id', reactionId)
    if (error) throw error
  }

  // -- Payment
  async getCouponCodes(): Promise<CouponCode[]> {
    const { data, error } = await supabase.from('coupon_codes').select('*')
    if (error) throw error
    if (!data) return []
    return data.map((couponCode) => CouponCode.fromPG(couponCode))
  }

  // -- Notifications
  async getNotifications(userID: string): Promise<Notification[]> {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userID)
      .order('created_at', { ascending: false })

    if (error) throw error
    if (!data) return []
    return data.map((notification) => Notification.fromPG(notification))
  }

  async saveNotification(notification: Notification): Promise<void> {
    const { error } = await supabase.from('notifications').upsert(notification.toPG(), {
      onConflict: 'id',
    })
    if (error) throw error
  }

  // -- Announcements
  async getAnnouncements(challengeID?: string): Promise<Announcement[]> {
    let query = supabase.from('announcements').select('*').order('created_at', { ascending: false })
    if (challengeID) {
      query = query.eq('challenge_id', challengeID)
    }
    const { data, error } = await query
    if (error) throw error
    if (!data) return []
    return data.map((announcement) => Announcement.fromPG(announcement))
  }

  // -- Events
  async getEvents(challengeID: string): Promise<AppEvent[]> {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('active', true)
      .eq('challenge_id', challengeID)
      .order('created_at', { ascending: false })
    if (error) throw error
    if (!data) return []
    return data.map((event) => AppEvent.fromPG(event))
  }

  async saveUserEvent(userID: string, eventID: string, response: string): Promise<void> {
    const { error } = await supabase.from('users_events').upsert(
      {
        user_id: userID,
        event_id: eventID,
        response,
      },
      {
        onConflict: 'user_id,event_id',
      },
    )
    if (error) throw error
  }

  async getUserEvents(userID: string): Promise<Array<{ eventID: string; response: string }>> {
    const { data, error } = await supabase
      .from('users_events')
      .select('event_id, response')
      .eq('user_id', userID)
    if (error) throw error
    if (!data) return []
    return data.map((item) => ({
      eventID: item.event_id,
      response: item.response,
    }))
  }

  // -- Badges
  async getBadges(challengeID: string): Promise<Badge[]> {
    const { data, error } = await supabase
      .from('badges')
      .select('*')
      .eq('challenge_id', challengeID)
    if (error) throw error
    if (!data) return []
    return data.map((badge) => Badge.fromPG(badge))
  }

  // -- getUserBadges
  async getUserBadges(
    userID: string,
    challengeID?: string,
  ): Promise<import('../../model/Badge').EarnedBadge[]> {
    const { data, error } = await supabase
      .from('user_badges')
      .select('badges(*), earned_at')
      .eq('user_id', userID)
    if (error) throw error
    if (!data) return []
    return data
      .map((row) => ({
        badge: Badge.fromPG(row.badges),
        earnedAt: new Date(row.earned_at ?? Date.now()),
      }))
      .filter((row) => !challengeID || row.badge.challengeID === challengeID)
  }

  // -- saveUserBadge
  async saveUserBadge(userID: string, badgeID: string, earnedAt: Date): Promise<void> {
    const { error } = await supabase.from('user_badges').insert({
      user_id: userID,
      badge_id: badgeID,
      earned_at: earnedAt.toISOString(),
    })
    if (error) throw error
  }

  // -- getTeamBadges
  async getTeamBadges(teamID: string): Promise<import('../../model/Badge').EarnedBadge[]> {
    const { data, error } = await supabase
      .from('team_badges')
      .select('badges(*), earned_at')
      .eq('team_id', teamID)
    if (error) throw error
    if (!data) return []
    return data.map((row) => ({
      badge: Badge.fromPG(row.badges),
      earnedAt: new Date(row.earned_at ?? Date.now()),
    }))
  }

  // -- saveTeamBadge
  async saveTeamBadge(
    teamID: string,
    badgeID: string,
    earnedAt: Date,
    earnedByUserID: string | null,
  ): Promise<void> {
    const { error } = await supabase.from('team_badges').insert({
      team_id: teamID,
      badge_id: badgeID,
      earned_at: earnedAt.toISOString(),
      earned_by_user_id: earnedByUserID,
    })
    if (error) throw error
  }

  // -- Rest Days
  async getRestDays(userID: string, challengeID: string): Promise<RestDay[]> {
    const { data, error } = await supabase
      .from('users_rest_days')
      .select('*')
      .eq('user_id', userID)
      .eq('challenge_id', challengeID)
      .order('start_time', { ascending: false })
    if (error) throw error
    if (!data) return []
    return data.map((restDay) => RestDay.fromPG(restDay))
  }

  async saveRestDay(restDay: RestDay): Promise<void> {
    const { error } = await supabase.from('users_rest_days').insert(restDay.toPG())
    if (error) throw error
  }

  // -- Checkpoints
  async getCheckpoints(challengeID: string): Promise<Checkpoint[]> {
    const { data, error } = await supabase
      .from('checkpoints')
      .select('*')
      .eq('challenge_id', challengeID)
      .order('sort_order', { ascending: true })
    if (error) throw error
    if (!data) return []
    return data.map((row) => Checkpoint.fromPG(row))
  }

  // -- Gates
  async getGates(challengeID: string): Promise<Gate[]> {
    const { data, error } = await supabase
      .from('gates')
      .select('*, checkpoints(*)')
      .eq('challenge_id', challengeID)
    if (error) throw error
    if (!data) return []

    return data
      .map((row) => Gate.fromPG(row))
      .sort((a, b) => a.checkpoint.sortOrder - b.checkpoint.sortOrder)
  }

  async getGateSubmissions(gateID: string, teamID: string): Promise<GateSubmission[]> {
    const { data, error } = await supabase
      .from('gate_submissions')
      .select('*')
      .eq('gate_id', gateID)
      .eq('team_id', teamID)
      .order('created_at', { ascending: false })
    if (error) throw error
    if (!data) return []
    return data.map((row) => GateSubmission.fromPG(row))
  }

  async getTeamGateSubmissions(teamID: string): Promise<GateSubmission[]> {
    const { data, error } = await supabase
      .from('gate_submissions')
      .select('*')
      .eq('team_id', teamID)
    if (error) throw error
    if (!data) return []
    return data.map((row) => GateSubmission.fromPG(row))
  }

  async saveGateSubmission(submission: GateSubmission): Promise<GateSubmission> {
    const { data, error } = await supabase
      .from('gate_submissions')
      .upsert(submission.toPG(), { onConflict: 'gate_id,team_id,user_id' })
      .select()
      .single()
    if (error) throw error
    return GateSubmission.fromPG(data)
  }

  // -- User engagements
  async getUserEngagements(userID: string): Promise<UserEngagement[]> {
    const { data, error } = await supabase
      .from('user_engagements')
      .select('*')
      .eq('user_id', userID)
    if (error) throw error
    if (!data) return []
    return data.map((row) => UserEngagement.fromPG(row))
  }

  async saveUserEngagement(engagement: UserEngagement): Promise<UserEngagement> {
    const { data, error } = await supabase
      .from('user_engagements')
      .upsert(engagement.toPG(), { onConflict: 'user_id,subject_type,subject_id' })
      .select()
      .single()
    if (error) throw error
    return UserEngagement.fromPG(data)
  }

  async logError(entry: ErrorLogEntry): Promise<void> {
    try {
      let userId = entry.userId
      if (userId === undefined) {
        const {
          data: { user },
        } = await supabase.auth.getUser()
        userId = user?.id ?? null
      }

      await supabase.from('error_logs').insert({
        message: entry.message,
        stack: entry.stack ?? undefined,
        context: entry.context && Object.keys(entry.context).length > 0 ? entry.context : undefined,
        user_id: userId,
        url: entry.url ?? (typeof window !== 'undefined' ? window.location.href : undefined),
        user_agent:
          entry.userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : undefined),
        source: entry.source ?? 'client',
      })
    } catch {
      // Do not throw; logging must not break the app.
    }
  }
}
